import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';

export interface ICameraDevice {
  // HAT Cloud fields
  terminalID: string;
  serialNumber: string;
  deviceModelCode: string;
  deviceStatus: number; // 0=offline, 1=online
  createTime: string;
  version?: string;
  productKey: string;
  protocol: string;
  language: string;
  timeZone: number;
  deviceChannelList?: Array<{
    channelNum: number;
    channelType: number;
    channelName: string;
    channelMum?: number;
  }>;
  disabledChannelNumbers?: number[];
  
  // Local mapping fields
  licensePlate: string;
  companyName: string;
  companyId: number | null;
  truckImplementId: number | null;
  mappingId: number | null;
  active: boolean;
}

const initialState: EntityState<ICameraDevice> = {
  loading: false,
  errorMessage: null,
  entities: [] as ICameraDevice[],
  entity: {} as ICameraDevice,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/admin/camera-devices';

let cancelTokenSource = axios.CancelToken.source();

export const getEntities = createAsyncThunk(
  'cameraDevice/fetch_entity_list',
  async ({ page, size, sort }: IQueryParams) => {
    const requestUrl = `${apiUrl}?page=${page ?? 0}&size=${size ?? 20}${sort ? `&sort=${sort}` : ''}&cacheBuster=${new Date().getTime()}`;

    cancelTokenSource.cancel('New request started.');
    cancelTokenSource = axios.CancelToken.source();

    return axios.get<ICameraDevice[]>(requestUrl, { cancelToken: cancelTokenSource.token });
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'cameraDevice/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICameraDevice>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'cameraDevice/create_entity',
  async (entity: ICameraDevice) => {
    return axios.post<ICameraDevice>(apiUrl, entity);
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'cameraDevice/update_entity',
  async (entity: ICameraDevice) => {
    return axios.put<ICameraDevice>(`${apiUrl}/${entity.mappingId}`, entity);
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'cameraDevice/delete_entity',
  async ({ id }: { id: string | number }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ICameraDevice>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const activateEntity = createAsyncThunk(
  'cameraDevice/activate_entity',
  async (id: string | number) => {
    // First, fetch the device to get its current data
    const deviceUrl = `${apiUrl}/${id}`;
    const deviceResponse = await axios.get<ICameraDevice>(deviceUrl);
    const device = deviceResponse.data;

    // Fetch models to find modelId by productKey
    const modelsUrl = '/api/admin/camera-device-models/active';
    const modelsResponse = await axios.get(modelsUrl);
    const models = modelsResponse.data || [];
    const matchingModel = models.find((m: any) => m.productKey === device.productKey);
    const modelId = matchingModel?.id;

    // Prepare activation request with required fields
    const activationRequest = {
      modelId: modelId || null,
      timeZone: device.timeZone,
      channelCount: (device.deviceChannelList?.length || 4),
      companyId: device.companyId,
      truckImplementId: device.truckImplementId,
    };

    // Activate the device
    const requestUrl = `${apiUrl}/${id}/activate`;
    return axios.post<ICameraDevice>(requestUrl, activationRequest);
  },
  { serializeError: serializeAxiosError }
);

const extraReducers = (builder: any) => {
  builder
    .addMatcher(isPending(getEntities), state => {
      state.loading = true;
    })
    .addMatcher(isFulfilled(getEntities), (state, action) => {
      const { data, headers } = action.payload;
      state.loading = false;
      // API returns {status, data: {results: [], totalRecords: N}}
      state.entities = data?.data?.results || data || [];
      state.totalItems = data?.data?.totalRecords || parseInt(headers['x-total-count'], 10) || 0;
    })
    .addMatcher(isPending(getEntity), state => {
      state.loading = true;
    })
    .addMatcher(isFulfilled(getEntity), (state, action) => {
      state.loading = false;
      state.entity = action.payload.data;
    })
    .addMatcher(isPending(createEntity, updateEntity, deleteEntity, activateEntity), state => {
      state.updating = true;
    })
    .addMatcher(isFulfilled(createEntity, updateEntity, deleteEntity, activateEntity), (state, action) => {
      state.updating = false;
      state.updateSuccess = true;
    });
};

export const slice = createEntitySlice({
  name: 'cameraDevice',
  initialState,
  extraReducers,
  reducers: {
    reset() {
      return initialState;
    },
    clearEntities(state) {
      state.entities = [];
      state.totalItems = 0;
    },
  },
});

export const { reset, clearEntities } = slice.actions;

// Reducer
export default slice.reducer;
